import React, { useEffect } from 'react';
import type { BrannanWebsiteHeaderSettings } from '@shared/types/WebsiteHeaderSettings.types';
import type { EventNameNavProps } from './components/EventNameNav';
import { isInIframe } from '@shared/utils/isInIframe';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { LazyCenterLayout } from './layouts/Center.lazy';
import { LazyFullWidthLayout } from './layouts/FullWidth.lazy';
import { LazyPageWidthLayout } from './layouts/PageWidth.lazy';
import { LazyNoPhotoLayout } from './layouts/NoPhoto.lazy';
import { LazyPhotoArchLayout } from './layouts/PhotoArch.lazy';
import { LazyPhotoDuoLeftLayout } from './layouts/PhotoDuoLeft.lazy';
import { LazyPhotoDuoRightLayout } from './layouts/PhotoDuoRight.lazy';
import { LazyPhotoMarqueeLayout } from './layouts/PhotoMarquee.lazy';
import { LazyPhotoMarqueePageWidthLayout } from './layouts/PhotoMarqueePageWidth.lazy';
import { LazyPhotoSliderLayout } from './layouts/PhotoSlider.lazy';
import { LazyPhotoSliderPageWidthLayout } from './layouts/PhotoSliderPageWidth.lazy';
import { LazyPhotoTrioLayout } from './layouts/PhotoTrio.lazy';
import { LazyMonogramLayout } from './layouts/Monogram.lazy';
import { LazyPhotoTrioPageWidthLayout } from './layouts/PhotoTrioPageWidth.lazy';
import { LazyVideoArchLayout } from './layouts/VideoArchLayout.lazy';
import { isMastheadCompatibleLayout } from '@shared/utils/mastheadCompat';

export interface HeaderV2Props extends EventNameNavProps {
  websiteHeader: NonNullable<BrannanWebsiteHeaderSettings['websiteHeader']>;
  pageId?: string;
}

const isPreview = isInIframe();

/**
 * This set of header layouts utilizes the new `websiteHeader` object.
 *
 * Each layout will render:
 * - Title
 * - Nav
 * - Header Photo
 */
export const HeaderV2 = (props: HeaderV2Props) => {
  const { websiteHeader, masthead, ...restPropsWithoutAccent } = props;

  const headerPhoto = websiteHeader.headerPhoto;
  const showPlaceholder = isPreview && !!headerPhoto.presentationLayout;

  const restProps = {
    ...restPropsWithoutAccent,
    masthead: isMastheadCompatibleLayout(headerPhoto.presentationLayout) ? masthead : null,
  };

  useEffect(() => {
    if (headerPhoto.presentationLayout) {
      sendMessageToParentWindow({ action: 'requestIframeScaleValue', source: 'joyPreview' });
    }
  }, [headerPhoto.presentationLayout]);

  switch (headerPhoto.presentationLayout) {
    // have to rebuild websiteHeader explicitly because despite the switch,
    // TSC complains about the discriminated union whose shared type is 2 levels deep

    case 'center':
      return (
        <LazyCenterLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );
    case 'fullWidth':
      return (
        <LazyFullWidthLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );
    case 'pageWidth':
      return (
        <LazyPageWidthLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'photoSlider':
      return (
        <LazyPhotoSliderLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'photoSliderPageWidth':
      return (
        <LazyPhotoSliderPageWidthLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'photoTrio':
      return (
        <LazyPhotoTrioLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'photoTrioPageWidth':
      return (
        <LazyPhotoTrioPageWidthLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'noPhoto':
      return (
        <LazyNoPhotoLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'monogram':
      return (
        <LazyMonogramLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'photoArch':
      return (
        <LazyPhotoArchLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'photoDuoLeft':
      return (
        <LazyPhotoDuoLeftLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'photoDuoRight':
      return (
        <LazyPhotoDuoRightLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'photoMarquee':
      return (
        <LazyPhotoMarqueeLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'photoMarqueePageWidth':
      return (
        <LazyPhotoMarqueePageWidthLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'videoArch':
      return (
        <LazyVideoArchLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    default:
      return null;
  }
};

export default HeaderV2;
